/**
 * Synchronous SHA-256 and HMAC-SHA-256.
 *
 * Implemented locally (rather than via WebCrypto) because the provably-fair
 * generator must be synchronous inside game loops and testable under Node
 * without an async crypto dependency. Verified against FIPS 180-4 / RFC 4231
 * test vectors in `hash.test.ts`.
 *
 * All primitives operate on bytes internally; the string entry points encode
 * as UTF-8 first. Never round-trip digests through latin1 strings.
 */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const INIT = new Uint32Array([
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
]);

function rotr(x: number, n: number): number {
  return (x >>> n) | (x << (32 - n));
}

export function utf8Bytes(input: string): Uint8Array {
  return new TextEncoder().encode(input);
}

/** Core SHA-256 compression over raw bytes. */
export function sha256Digest(bytes: Uint8Array): Uint8Array {
  const bitLen = bytes.length * 8;
  const paddedLen = (((bytes.length + 9) >> 6) + 1) << 6;
  const buffer = new ArrayBuffer(paddedLen);
  const block = new Uint8Array(buffer);
  block.set(bytes);
  block[bytes.length] = 0x80;

  const view = new DataView(buffer);
  view.setUint32(paddedLen - 8, Math.floor(bitLen / 0x100000000), false);
  view.setUint32(paddedLen - 4, bitLen >>> 0, false);

  const h = new Uint32Array(INIT);
  const w = new Uint32Array(64);

  for (let offset = 0; offset < paddedLen; offset += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4, false);
    for (let i = 16; i < 64; i++) {
      const v15 = w[i - 15]!;
      const v2 = w[i - 2]!;
      const s0 = rotr(v15, 7) ^ rotr(v15, 18) ^ (v15 >>> 3);
      const s1 = rotr(v2, 17) ^ rotr(v2, 19) ^ (v2 >>> 10);
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0;
    }

    let a = h[0]!;
    let b = h[1]!;
    let c = h[2]!;
    let d = h[3]!;
    let e = h[4]!;
    let f = h[5]!;
    let g = h[6]!;
    let hh = h[7]!;

    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + K[i]! + w[i]!) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }

    h[0] = (h[0]! + a) >>> 0;
    h[1] = (h[1]! + b) >>> 0;
    h[2] = (h[2]! + c) >>> 0;
    h[3] = (h[3]! + d) >>> 0;
    h[4] = (h[4]! + e) >>> 0;
    h[5] = (h[5]! + f) >>> 0;
    h[6] = (h[6]! + g) >>> 0;
    h[7] = (h[7]! + hh) >>> 0;
  }

  // Serialise the state big-endian, as required by FIPS 180-4.
  const digest = new Uint8Array(32);
  const out = new DataView(digest.buffer);
  for (let i = 0; i < 8; i++) out.setUint32(i * 4, h[i]!, false);
  return digest;
}

export function toHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) out += bytes[i]!.toString(16).padStart(2, '0');
  return out;
}

export function fromHex(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/** SHA-256 over the UTF-8 encoding of `message`, lowercase hex. */
export function sha256(message: string): string {
  return toHex(sha256Digest(utf8Bytes(message)));
}

/** SHA-256 digest bytes of the UTF-8 encoding of `message`. */
export function sha256Bytes(message: string): Uint8Array {
  return sha256Digest(utf8Bytes(message));
}

function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length + b.length);
  out.set(a);
  out.set(b, a.length);
  return out;
}

/** HMAC-SHA-256 returning raw digest bytes (RFC 2104). */
export function hmacSha256Bytes(
  key: string | Uint8Array,
  message: string | Uint8Array,
): Uint8Array {
  const BLOCK = 64;
  let keyBytes = typeof key === 'string' ? utf8Bytes(key) : key;
  if (keyBytes.length > BLOCK) keyBytes = sha256Digest(keyBytes);

  const padded = new Uint8Array(BLOCK);
  padded.set(keyBytes);

  const iKey = new Uint8Array(BLOCK);
  const oKey = new Uint8Array(BLOCK);
  for (let i = 0; i < BLOCK; i++) {
    iKey[i] = padded[i]! ^ 0x36;
    oKey[i] = padded[i]! ^ 0x5c;
  }

  const msgBytes = typeof message === 'string' ? utf8Bytes(message) : message;
  const inner = sha256Digest(concat(iKey, msgBytes));
  return sha256Digest(concat(oKey, inner));
}

/** HMAC-SHA-256(key, message) as lowercase hex. */
export function hmacSha256(key: string, message: string): string {
  return toHex(hmacSha256Bytes(key, message));
}

/** Cryptographically strong hex seed. */
export function randomSeed(bytes = 32): string {
  const arr = new Uint8Array(bytes);
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    globalThis.crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < arr.length; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  return toHex(arr);
}
